/* Core compliance UI primitives:
 *   - ComplianceDisclaimer   — contextual text with visual variant
 *   - AIBadge                — "AI-generated" chip with tooltip
 *   - EvidenceBadge          — "Validated by research" chip
 *   - RiskIndicator          — Moderate/High risk chip
 *
 * These are intentionally framework-lite (no shadcn Popover dependency) so
 * they can be safely embedded inside existing panels without bloat.
 */
import React, { useState } from "react";
import { useDisclaimer, useFeatureDisclaimer } from "@/lib/compliance";
import { useLanguage } from "@/i18n/LanguageContext";
import { Info, Sparkles, ShieldCheck, AlertTriangle, Microscope } from "lucide-react";

const VARIANT_STYLES = {
  light:    { wrap: "border-sky-200 bg-sky-50 text-sky-900",       icon: "text-sky-500",    Icon: Info },
  moderate: { wrap: "border-amber-200 bg-amber-50 text-amber-900", icon: "text-amber-500",  Icon: AlertTriangle },
  strong:   { wrap: "border-orange-300 bg-orange-50 text-orange-900", icon: "text-orange-500", Icon: AlertTriangle },
  full:     { wrap: "border-rose-300 bg-rose-50 text-rose-900",    icon: "text-rose-500",   Icon: AlertTriangle },
};

/**
 * ComplianceDisclaimer — shows the contextual disclaimer text.
 *
 * Props:
 *   context    — disclaimer key (chat/lab/clinical/…) takes precedence over featureId
 *   featureId  — map a pricing feature id → context
 *   compact    — one-line inline variant (no icon wrap)
 */
export function ComplianceDisclaimer({ context, featureId, compact = false, className = "" }) {
  const ctxDisc = useDisclaimer(context || "general");
  const featDisc = useFeatureDisclaimer(featureId);
  const d = context ? ctxDisc : (featureId ? featDisc : ctxDisc);
  if (!d?.text) return null;
  const v = VARIANT_STYLES[d.variant] || VARIANT_STYLES.light;
  const Icon = v.Icon;

  if (compact) {
    return (
      <span
        data-testid={`compliance-disclaimer-${context || featureId || "general"}`}
        className={`inline-flex items-center gap-1 text-[11px] ${v.icon} ${className}`}
      >
        <Icon size={11} />
        <span className="opacity-90">{d.text}</span>
      </span>
    );
  }
  return (
    <div
      data-testid={`compliance-disclaimer-${context || featureId || "general"}`}
      className={`rounded-md border text-xs px-3 py-2 flex items-start gap-2 ${v.wrap} ${className}`}
    >
      <Icon size={14} className={`mt-0.5 shrink-0 ${v.icon}`} />
      <span className="leading-snug">{d.text}</span>
    </div>
  );
}

/** AIBadge — "AI-generated" pill with hover tooltip. */
export function AIBadge({ className = "" }) {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const bg = language === "bg";
  const label = bg ? "AI-генерирано" : "AI-generated";
  const tip = bg
    ? "Резултатът е създаден от AI модел. Не е медицинско мнение."
    : "Generated by an AI model. Not a medical opinion.";
  return (
    <span
      data-testid="ai-badge"
      className={`relative inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700 ${className}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      tabIndex={0}
    >
      <Sparkles size={10} />
      {label}
      {show && (
        <span
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-[220px] rounded-md border border-gray-200 bg-white p-2 text-[11px] text-gray-700 shadow-lg"
        >
          {tip}
        </span>
      )}
    </span>
  );
}

/** EvidenceBadge — "Validated by research" pill. Used when the output is
 *  accompanied by an evidence unlock or a verified PubMed study. */
export function EvidenceBadge({ className = "" }) {
  const { language } = useLanguage();
  const bg = language === "bg";
  return (
    <span
      data-testid="evidence-badge"
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ${className}`}
      title={bg ? "Подкрепено от научни проучвания" : "Backed by scientific research"}
    >
      <Microscope size={10} />
      {bg ? "Валидирано" : "Validated"}
    </span>
  );
}

/** RiskIndicator — shows AI risk-level chip (internal self-assessment). */
export function RiskIndicator({ level = "moderate", className = "" }) {
  const { language } = useLanguage();
  const bg = language === "bg";
  const map = {
    low:      { label: bg ? "Нисък риск" : "Low risk",           css: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    moderate: { label: bg ? "Умерен риск" : "Moderate risk",     css: "border-amber-200 bg-amber-50 text-amber-700" },
    high:     { label: bg ? "Висок риск" : "High risk",          css: "border-rose-200 bg-rose-50 text-rose-700" },
  };
  const cfg = map[level] || map.moderate;
  return (
    <span
      data-testid={`risk-indicator-${level}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.css} ${className}`}
      title={bg ? "Вътрешна класификация на риска (EU AI Act)" : "Internal risk classification (EU AI Act)"}
    >
      <ShieldCheck size={10} />
      {cfg.label}
    </span>
  );
}
